import { type Editor } from '@tiptap/core';
import {
  exitSuggestion,
  type SuggestionKeyDownProps,
  type SuggestionOptions,
  type SuggestionProps,
} from '@tiptap/suggestion';
import { type RefObject, useCallback, useMemo, useRef, useState } from 'react';

import { type WorkspaceAgentComposerSkillInsertSource } from '@air/analytics';

import {
  WORKSPACE_AGENT_SKILL_LIST_ID,
  type WorkspaceAgentSkillTriggerMenuPopupRef,
  type WorkspaceAgentSkillTriggerMenuSessionProps,
} from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/skills/WorkspaceAgentSkillTriggerMenu';
import { usePrivateWorkspaceAgentSkillsCatalogPagination } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/usePrivateWorkspaceAgentSkillsCatalogPagination';
import { usePrivateWorkspaceAgentSkillsCatalog } from '~/components/v2/components/workspace/workspace-agent-panel/PrivateWorkspaceAgentSkillsCatalogProvider';
import { usePrivateWorkspaceAgentActionsMenuSkillsSearch } from '~/components/v2/components/workspace/workspace-agent-panel/queries/usePrivateWorkspaceAgentActionsMenuSkillsSearch';
import {
  buildWorkspaceAgentSkillSuggestionItems,
  WORKSPACE_AGENT_SKILL_REOPEN_META,
  WORKSPACE_AGENT_SKILL_SUGGESTION_PLUGIN_KEY,
  type WorkspaceAgentSkillSuggestionExtensionOptions,
} from '~/components/v2/components/workspace/workspace-agent-panel/slash-commands/workspaceAgentSkillSuggestion';
import { insertWorkspaceAgentSkillMonikerInEditor } from '~/components/v2/components/workspace/workspace-agent-panel/utils/insertWorkspaceAgentSkillMonikerInEditor';
import { type Skill } from '~/hooks/queries/skills/usePrivateSkillsList';

type WorkspaceAgentSkillSuggestionSessionState = {
  isOpen: boolean;
  query: string;
};

const CLOSED_SESSION_STATE: WorkspaceAgentSkillSuggestionSessionState = {
  isOpen: false,
  query: '',
};

const WORKSPACE_AGENT_SKILL_TRIGGER_CHAR = '/';

export type UsePrivateWorkspaceAgentSkillSuggestionStateParams = {
  areSlashSkillsEnabled?: boolean;
  isCreditsDepleted?: boolean;
  onSkillMonikerInserted?: (
    skill: Pick<Skill, 'id' | 'scope' | 'type'>,
    insertSource: WorkspaceAgentComposerSkillInsertSource,
  ) => void;
};

export type UsePrivateWorkspaceAgentSkillSuggestionStateResult = {
  editorAriaAttributes: Record<string, string>;
  getSuggestionClientRect: () => DOMRect | null;
  isSlashSkillsEnabled: () => boolean;
  /** Types `/` (with a leading space when needed) so the slash suggestion plugin opens. */
  openManualSlashPicker: (editor: Editor | null | undefined) => void;
  popupProps: WorkspaceAgentSkillTriggerMenuSessionProps | null;
  popupRef: RefObject<WorkspaceAgentSkillTriggerMenuPopupRef>;
  skillSuggestionOptions: WorkspaceAgentSkillSuggestionExtensionOptions['suggestion'];
};

/**
 * Mirrors `@` mention suggestion: TipTap signals the `/` session; the menu lists workspace skills
 * from `Skills.list` (all scopes), filters loaded rows by the typed `/` query, and appends
 * server-side query matches from catalog pages that have not loaded yet.
 */
export const usePrivateWorkspaceAgentSkillSuggestionState = ({
  areSlashSkillsEnabled = true,
  isCreditsDepleted = false,
  onSkillMonikerInserted,
}: UsePrivateWorkspaceAgentSkillSuggestionStateParams): UsePrivateWorkspaceAgentSkillSuggestionStateResult => {
  const { isSkillsListLoading, isWorkspaceAgentSlashSkillsEnabled, skills, workspaceId } =
    usePrivateWorkspaceAgentSkillsCatalog();
  const {
    hasSkillsListNextPage,
    isSkillsListFetchNextPageError,
    isSkillsListFetchingNextPage,
    requestSkillsListNextPage,
    retrySkillsListNextPage,
  } = usePrivateWorkspaceAgentSkillsCatalogPagination();
  const skillsRef = useRef(skills);
  skillsRef.current = skills;

  const [session, setSession] = useState<WorkspaceAgentSkillSuggestionSessionState>(CLOSED_SESSION_STATE);
  const [activeOptionId, setActiveOptionId] = useState<string | undefined>(undefined);
  const popupRef = useRef<WorkspaceAgentSkillTriggerMenuPopupRef>(null);
  const sessionRef = useRef<SuggestionProps<Skill, Skill>>();
  const onSkillMonikerInsertedRef = useRef(onSkillMonikerInserted);
  onSkillMonikerInsertedRef.current = onSkillMonikerInserted;

  const areSlashSkillsEnabledRef = useRef(areSlashSkillsEnabled);
  areSlashSkillsEnabledRef.current = areSlashSkillsEnabled;

  const isEnabled = isWorkspaceAgentSlashSkillsEnabled && !isCreditsDepleted && areSlashSkillsEnabled;
  const isEnabledRef = useRef(isEnabled);
  isEnabledRef.current = isEnabled;

  const { isLoading: isSkillsSearchLoading, skills: searchedSkills } = usePrivateWorkspaceAgentActionsMenuSkillsSearch({
    enabled: isEnabled && session.isOpen,
    query: session.query,
    workspaceId,
  });
  const searchedSkillsRef = useRef(searchedSkills);
  searchedSkillsRef.current = searchedSkills;

  const isSlashSkillsEnabled = useCallback(() => isEnabledRef.current, []);

  const dismissFromPortal = useCallback(() => {
    const editor = sessionRef.current?.editor;

    if (editor) {
      exitSuggestion(editor.view, WORKSPACE_AGENT_SKILL_SUGGESTION_PLUGIN_KEY);
    }
  }, []);

  const handleSuggestionSession = useCallback((props: SuggestionProps<Skill, Skill>) => {
    sessionRef.current = props;
    setSession({ isOpen: true, query: props.query });
  }, []);

  const handleSuggestionExit = useCallback(() => {
    sessionRef.current = undefined;
    setActiveOptionId(undefined);
    setSession(CLOSED_SESSION_STATE);
  }, []);

  const handleActiveDescendantChange = useCallback((optionId: string | undefined) => {
    setActiveOptionId(optionId);
  }, []);

  const skillSuggestionOptions: Omit<SuggestionOptions<Skill, Skill>, 'editor'> = useMemo(
    () => ({
      command: ({ editor, range, props: skill }) => {
        insertWorkspaceAgentSkillMonikerInEditor(editor, skill, {
          deleteRange: range,
          onInserted: onSkillMonikerInsertedRef.current
            ? (insertedSkill) => onSkillMonikerInsertedRef.current?.(insertedSkill, 'slash_menu')
            : undefined,
        });
      },

      allow: () => isEnabledRef.current,

      items: ({ query }) =>
        buildWorkspaceAgentSkillSuggestionItems({
          loadedSkills: skillsRef.current,
          query,
          searchedSkills: searchedSkillsRef.current,
        }),

      shouldResetDismissed: ({ transaction }) =>
        transaction.docChanged || transaction.getMeta(WORKSPACE_AGENT_SKILL_REOPEN_META) === true,

      render: () => ({
        onExit: handleSuggestionExit,
        onKeyDown: ({ event }: SuggestionKeyDownProps) => popupRef.current?.onKeyDown(event) ?? false,
        onStart: handleSuggestionSession,
        onUpdate: handleSuggestionSession,
      }),
    }),
    [handleSuggestionExit, handleSuggestionSession],
  );

  const handleSelectSkill = useCallback((skill: Skill) => {
    sessionRef.current?.command(skill);
  }, []);

  const openManualSlashPicker = useCallback((editor: Editor | null | undefined) => {
    if (!isEnabledRef.current || !editor || editor.isDestroyed) {
      return;
    }

    if (WORKSPACE_AGENT_SKILL_SUGGESTION_PLUGIN_KEY.getState(editor.state)?.active) {
      return;
    }

    editor.view.focus();

    if (!editor.state.selection.empty) {
      editor.commands.setTextSelection(editor.state.selection.to);
    }

    const { from } = editor.state.selection;
    const characterBefore = editor.state.doc.textBetween(Math.max(from - 1, 0), from);
    const characterAfter = editor.state.doc.textBetween(from, Math.min(from + 1, editor.state.doc.content.size));

    if (characterBefore === WORKSPACE_AGENT_SKILL_TRIGGER_CHAR) {
      editor.view.dispatch(editor.state.tr.setMeta(WORKSPACE_AGENT_SKILL_REOPEN_META, true));

      return;
    }

    // `WorkspaceAgentSkillSuggestion` only allows `/` after whitespace — mirror the @ manual picker.
    const isAfterWord = !!characterBefore && !/\s/.test(characterBefore);
    const isAtWordEnd = !characterAfter || /\s/.test(characterAfter);
    const insertedText =
      isAfterWord && isAtWordEnd ? ` ${WORKSPACE_AGENT_SKILL_TRIGGER_CHAR}` : WORKSPACE_AGENT_SKILL_TRIGGER_CHAR;

    editor.chain().insertContent(insertedText).scrollIntoView().run();
  }, []);

  const popupProps = useMemo((): WorkspaceAgentSkillTriggerMenuSessionProps | null => {
    if (!isEnabled || !session.isOpen) {
      return null;
    }

    const isQuerySearch = session.query.length > 0;
    const filteredItems = buildWorkspaceAgentSkillSuggestionItems({
      loadedSkills: skills,
      query: session.query,
      searchedSkills,
    });

    return {
      command: handleSelectSkill,
      dismissFromPortal,
      // A typed query is answered by `Skills.list({ query })`, which searches every page.
      // Keep catalog pagination for the empty `/` browse, where the menu is the loaded pages.
      hasSkillsListNextPage: isQuerySearch ? false : hasSkillsListNextPage,
      isLoading: isSkillsListLoading || (isQuerySearch && isSkillsSearchLoading),
      isSkillsListFetchNextPageError: isQuerySearch ? false : isSkillsListFetchNextPageError,
      isSkillsListFetchingNextPage: isQuerySearch ? false : isSkillsListFetchingNextPage,
      items: filteredItems,
      // Key only on the typed `/` query so pagination appends rows without remounting the menu.
      menuListKey: session.query,
      onActiveDescendantChange: handleActiveDescendantChange,
      requestSkillsListNextPage,
      retrySkillsListNextPage,
    };
  }, [
    dismissFromPortal,
    handleActiveDescendantChange,
    handleSelectSkill,
    hasSkillsListNextPage,
    isEnabled,
    isSkillsListFetchNextPageError,
    isSkillsListFetchingNextPage,
    isSkillsListLoading,
    isSkillsSearchLoading,
    requestSkillsListNextPage,
    retrySkillsListNextPage,
    searchedSkills,
    session.isOpen,
    session.query,
    skills,
  ]);

  const editorAriaAttributes = useMemo((): Record<string, string> => {
    if (!session.isOpen || !isEnabled) {
      return {};
    }

    const attributes: Record<string, string> = {
      'aria-autocomplete': 'list',
      'aria-expanded': 'true',
      'aria-haspopup': 'listbox',
      'aria-controls': WORKSPACE_AGENT_SKILL_LIST_ID,
      role: 'combobox',
    };

    if (activeOptionId) {
      attributes['aria-activedescendant'] = activeOptionId;
    }

    return attributes;
  }, [activeOptionId, isEnabled, session.isOpen]);

  const getSuggestionClientRect = useCallback(() => sessionRef.current?.clientRect?.() ?? null, []);

  return {
    editorAriaAttributes,
    getSuggestionClientRect,
    isSlashSkillsEnabled,
    openManualSlashPicker,
    popupProps,
    popupRef,
    skillSuggestionOptions,
  };
};
